import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Dépannage menuiseries Sète' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Dépannage et réparation de menuiseries à Sète',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'City', name: 'Sète' },
  description: 'Réparation de baies coulissantes, volets roulants, vitrages, stores et fenêtres à Sète. Intervention 24-48h, 7j/7. Devis gratuit, garantie 10 ans.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR', description: 'Devis gratuit' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Le sel marin abîme-t-il les menuiseries à Sète ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, l\'air salin de Sète accélère la corrosion des pièces métalliques : roulettes de baie, rails aluminium, mécanismes de crémone. Lors de nos interventions, nous utilisons des pièces traitées anticorrosion et appliquons un entretien préventif (nettoyage rails, lubrification) pour protéger vos équipements.',
      },
    },
    {
      '@type': 'Question',
      name: 'Quels sont vos délais à Sète, y compris le week-end ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Nous intervenons à Sète sous 24 à 48h, 7 jours sur 7, week-ends et jours fériés inclus. Que vous soyez sur le Mont Saint-Clair, à la Pointe Courte ou dans le centre-ville, nous nous déplaçons rapidement. Pour une vitre brisée, appelez le 09 80 80 22 42.',
      },
    },
  ],
};

export default function SetePage() {
  return (
    <>
      <SEO
        title="Réparation baie coulissante Sète | DFPRO"
        description="Baie coulissante, volet roulant ou vitrage en panne à Sète ? Air salin et corrosion : nos techniciens interviennent 24-48h, 7j/7."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/depannage-menuiserie-sete/"
      />
      <ServicePageLayout
        heroImage="/images/ville-herault-01.jpg"
        title="Réparation de menuiseries et baie coulissante à Sète"
        subtitle="L'air salin met vos menuiseries à rude épreuve. Baies, volets, stores : nos techniciens interviennent à Sète, de la Pointe Courte au Mont Saint-Clair. Devis gratuit, 24-48h."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        ctaTitle="Menuiserie exposée au sel à Sète ?"
        ctaText="Devis gratuit, pièces anticorrosion, intervention 24-48h, garantie 10 ans. Nos techniciens se déplacent dans tout Sète, 7j/7."
        ctaServiceLabel="Découvrir nos prestations"
      >
        <p className="text-lg text-secondary-500 leading-relaxed">
          À Sète, l'air chargé en sel attaque les pièces métalliques de vos menuiseries : roulettes de baie qui grippent, axes de volet qui s'oxydent, rails qui se corrodent. Nos techniciens utilisent des pièces traitées anticorrosion et interviennent dans tous les quartiers. Appelez le <a href="tel:+33980802242" className="text-accent-500 font-semibold hover:underline">09 80 80 22 42</a>.
        </p>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/sete-baie-01.jpg"
              alt="Réparation de baie vitrée coulissante à Sète"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Baie coulissante corrodée par le sel à Sète</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Les baies vitrées sont partout à Sète, du centre-ville aux villas du quartier des Quilles. L'exposition constante au sel réduit leur durée de vie : les roulettes rouillent, le rail se pique, la baie finit par <Link to="/baie-coulissante-qui-coince-herault/" className="text-accent-500 font-semibold hover:underline">coincer ou dérailler</Link>.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              Nous remplaçons les <Link to="/changer-roulettes-galets-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">roulettes et galets</Link> par des modèles renforcés anticorrosion, réparons le <Link to="/reparation-rail-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">rail</Link> et assurons le <Link to="/reglage-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réglage</Link> complet. La <Link to="/serrure-cremone-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">serrure et crémone</Link> sont aussi des points sensibles : nous les changeons avant qu'elles ne grippent.
            </p>
            <Link to="/reparation-baie-coulissante-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Voir le dépannage baie coulissante <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Volet roulant : la panne numéro un à Sète</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Le vent marin qui balaie Sète fragilise les <Link to="/reparation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">volets roulants</Link>. Lame désaxée par une rafale, axe de tablier oxydé, <Link to="/reparation-moteur-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">moteur</Link> qui force et surchauffe : nous diagnostiquons sur place et réparons.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              Nous intervenons aussi sur les <Link to="/changer-sangle-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">sangles</Link> craquelées par les UV et le sel, et proposons la <Link to="/motorisation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">motorisation</Link> des volets manuels avec des moteurs protégés adaptés au littoral.
            </p>
            <Link to="/reparation-volet-roulant-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Voir le dépannage volet roulant <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/sete-volet-02.jpg"
              alt="Réparation de volet roulant à Sète"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/sete-vitrage-03.jpg"
              alt="Remplacement de vitrage à Sète"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Vitrage et fenêtre : étanchéité renforcée pour le littoral</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              <Link to="/double-vitrage-embue-herault/" className="text-accent-500 font-semibold hover:underline">Double vitrage embué</Link> par condensation saline, <Link to="/remplacement-vitre-cassee-herault/" className="text-accent-500 font-semibold hover:underline">vitre cassée</Link> par le vent : notre service <Link to="/vitrier-herault/" className="text-accent-500 font-semibold hover:underline">vitrier</Link> intervient en urgence. Nous posons du double vitrage haute isolation.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              Côté <Link to="/reparation-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">fenêtres</Link>, nous changeons les <Link to="/changer-joint-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">joints</Link> dégradés par l'humidité saline, réparons les <Link to="/changer-cremone-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">crémones</Link> grippées et assurons le <Link to="/reglage-oscillo-battant-herault/" className="text-accent-500 font-semibold hover:underline">réglage oscillo-battant</Link>.
            </p>
            <Link to="/vitrier-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Voir le service vitrier <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <h2>Intervention à Sète sous 24 à 48h</h2>
        <p>
          Nous couvrons l'intégralité de Sète : centre-ville, Mont Saint-Clair, Pointe Courte, les Quilles, la Corniche, quartier de la gare et tous les secteurs de l'île. Nous desservons également Balaruc-les-Bains, Balaruc-le-Vieux, Frontignan et Marseillan. Consultez aussi <Link to="/depannage-menuiserie-frontignan/" className="text-accent-500 font-semibold hover:underline">Frontignan</Link> et <Link to="/depannage-menuiserie-agde/" className="text-accent-500 font-semibold hover:underline">Agde</Link>. Retrouvez tous nos services sur la <Link to="/" className="text-accent-500 font-semibold hover:underline">page d'accueil</Link>.
        </p>
      </ServicePageLayout>
    </>
  );
}